import { FaEnvelope, FaPhone, FaBriefcase, FaFileAlt } from "react-icons/fa";

export const UserCard = ({ user, onApprove, onViewDocuments, isApproving }) => {
  const isApproved = user.status === "Approved";

  return (
    <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
      <div
        className={`h-2 ${isApproved ? "bg-green-500" : "bg-yellow-500"}`}
      ></div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D80000] to-[#A00000] rounded-full flex items-center justify-center text-white font-bold">
              {user.firstName?.charAt(0)}
              {user.lastName?.charAt(0)}
            </div>
            <div>
              <p className="text-xs text-gray-500 font-medium">
                ID: {String(user._id).slice(-6)}
              </p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold ${
              isApproved
                ? "bg-green-100 text-green-700"
                : "bg-yellow-100 text-yellow-700"
            }`}
          >
            {isApproved ? "✓ Approved" : "⏳ Pending"}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-4">
          {user.firstName} {user.lastName}
        </h3>

        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3 text-gray-600">
            <FaEnvelope className="text-[#D80000] flex-shrink-0" size={16} />
            <span className="text-sm truncate">{user.emailAddress}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-600">
            <FaPhone className="text-[#D80000] flex-shrink-0" size={16} />
            <span className="text-sm">{user.phoneNumber}</span>
          </div>
          <div className="flex items-center gap-3 text-gray-600">
            <FaBriefcase className="text-[#D80000] flex-shrink-0" size={16} />
            <span className="text-sm font-medium">{user.designation}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-50 text-blue-600 font-semibold rounded-lg hover:bg-blue-100 transition-colors"
            onClick={() => onViewDocuments([user.photo, user.ID])}
          >
            <FaFileAlt size={16} />
            <span>Documents</span>
          </button>
          <button
            className={`flex-1 px-4 py-2.5 font-semibold rounded-lg transition-all ${
              isApproved
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-md hover:shadow-lg"
            }`}
            onClick={() => onApprove(user._id)}
            disabled={isApproved || isApproving}
          >
            {isApproving ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Approving...</span>
              </span>
            ) : isApproved ? (
              "Approved ✓"
            ) : (
              "Approve"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DocumentModal = ({ documents, onClose, onImageClick }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gradient-to-r from-[#D80000] to-[#A00000]">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <FaFileAlt />
            Uploaded Documents
          </h2>
          <button
            className="text-white hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center transition-colors text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {documents?.length > 0 ? (
              documents.map((doc, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-xl border-2 border-gray-200 hover:border-[#D80000] transition-all cursor-pointer"
                  onClick={() => onImageClick(doc)}
                >
                  <img
                    src={doc}
                    alt={`Document ${index + 1}`}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                    <svg
                      className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                      />
                    </svg>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                    <p className="text-white text-sm font-medium">
                      Document {index + 1}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-2 text-center py-12">
                <FaFileAlt className="text-gray-300 text-6xl mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No documents found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const FullImageModal = ({ image, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-90 flex justify-center items-center z-50">
    <div className="relative w-full h-full p-4">
      <button
        className="absolute top-8 right-8 text-white hover:bg-white/20 rounded-full w-12 h-12 flex items-center justify-center transition-colors text-4xl font-bold z-10"
        onClick={onClose}
      >
        ×
      </button>
      <img
        src={image}
        alt="Full Size"
        className="w-full h-full object-contain"
      />
    </div>
  </div>
);
